'use client'
import { Button } from '@heroui/button'
import { Image } from '@heroui/image'
import { useDisclosure } from '@heroui/modal'
import { useState } from 'react'

import { CrossIcon } from '@/components/shared'
import { type GetImageArgs } from '@/data'
import { cn } from '@/utils'

import EncryptedImage from '../encrypted-image'
import ImageModal from '../image-modal'

const IMAGE_SIZE = 54

interface BaseImageListProps {
  onRemove?: (index: number) => void
  withPreview?: boolean
  isEditMode?: boolean
  initialImagesCount?: number
}

interface PlainImagesProps extends BaseImageListProps {
  images: string[]
  encryptedImages?: never
}

interface EncryptedImagesProps extends BaseImageListProps {
  images?: never
  encryptedImages: {
    original: GetImageArgs['image'][]
    thumbnail: GetImageArgs['image'][]
  }
}

type ImageListProps = PlainImagesProps | EncryptedImagesProps

const ImageList = ({
  images,
  encryptedImages,
  onRemove,
  withPreview = true,
}: ImageListProps) => {
  const { isOpen, onOpenChange } = useDisclosure()
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  const handleOnImageClick = (image: string) => {
    if (withPreview) {
      setPreviewImage(image)
      onOpenChange()
    }
  }

  return (
    <>
      <ul className="flex gap-2">
        {encryptedImages
          ? encryptedImages.original.map((original, index) => (
              <EncryptedImage
                key={original.path}
                original={original}
                thumbnail={encryptedImages.thumbnail[index]}
                withPreview={withPreview}
              />
            ))
          : images.map((image, index) => (
              <div
                key={image}
                className="relative"
                style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}
              >
                <Image
                  shadow="none"
                  src={image}
                  alt="moment-image"
                  width={IMAGE_SIZE}
                  height={IMAGE_SIZE}
                  style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}
                  classNames={{
                    wrapper: cn('relative rounded-2xl cursor-pointer'),
                    img: 'object-cover',
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleOnImageClick(image)
                  }}
                />
                {onRemove && (
                  <Button
                    onPress={() => onRemove(index)}
                    variant="solid"
                    color="danger"
                    className="absolute -right-1 -top-1 z-10 flex h-4 w-4 min-w-0 items-center justify-center rounded-full p-0"
                  >
                    <CrossIcon className="text-xs" />
                  </Button>
                )}
              </div>
            ))}
      </ul>
      <ImageModal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        image={previewImage ?? ''}
      />
    </>
  )
}

export default ImageList
